import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import ReactMapGL, { Marker } from 'react-map-gl';
import cn from 'classnames';
// import WhiteIcon from 'components/shared/salon-marker-white';
import GreenIcon from 'components/shared/salon-marker-black';
import KeuneIcon from 'components/shared/keune-icon-white';
import BackChevron from 'components/shared/back-chevron';
import DirectionRightIcon from 'components/shared/direction-right';
import Phone from 'components/shared/phone';
import SiteLink from 'components/shared/sitelink';
import WebsiteRedirect from 'components/shared/websiteRedirect';
import { handleGetDistance } from 'lib/utils/salonsHelpers';
import { useDidMountEffect } from 'hooks';
import useIsMobile from 'hooks/utils/useIsMobile';
import trackSelectedSalon from 'lib/utils/trackSelectedSalon';
import { useGlobalModules } from 'contexts';
import styles from './salonDetails.module.scss';

const SalonDetails = ({
  salon,
  active,
  hoverPin,
  closedText,
  activeFilters,
  selectedSalon,
  salonsInRadius,
  initialViewport,
  languageCode,
  setHovered,
  setShowSalon,
  setSalonFocus,
  setActiveFilters,
  setDisableSearch,
  setSalonsInRadius,
  setShowSuggestions,
  defaultLanguage,
}) => {
  const globalModules = useGlobalModules();
  const translations = globalModules?.translations?.salonFinder || {};

  const { backButtonContent, getDirectionsPointer, keuneAmbassadorSalonContent } = translations;

  const salonViewport = {
    width: '100%',
    height: '100%',
    longitude: salon?.position?.longitude,
    latitude: salon?.position?.latitude,
    zoom: 16,
  };
  const router = useRouter();
  const [viewport, setViewport] = useState(salonViewport);
  const [opening, setOpening] = useState('');

  const isMobile = useIsMobile();

  const addDistanceToSalons = salonsInRadius.map(singleSalon => {
    const formattedDistance = (
      handleGetDistance(
        {
          latitude: singleSalon?.position?.latitude,
          longitude: singleSalon?.position?.longitude,
        },
        initialViewport
      ) / 1000
    ).toFixed(1);

    return { ...singleSalon, formattedDistance };
  });

  const mapByDistance = addDistanceToSalons.sort(
    (salonA, salonB) => salonA.formattedDistance - salonB.formattedDistance
  );

  const idx = mapByDistance.findIndex(currentSalon => currentSalon.salonName === salon.salonName);

  const phoneQuery = salon?.phoneNumber
    ? salon?.phoneNumber
        .toString()
        .replace(/ *\([^)]*\) */g, '')
        .replace('+', '%2B')
        .split(' ')
        .join('')
    : '';

  const getOpeningTimes = (openingData, index) => {
    const text = openingData?.result?.opening_hours?.weekday_text[index]
      .split(':')
      .slice(1)
      .join(':');
    const result = text?.replace(/\s+/g, '') === 'Closed' ? closedText : text;

    return result;
  };

  useEffect(() => {
    const fetchData = async () => {
      const res = await fetch(
        `/api/opening-times?q=${phoneQuery}&l=${defaultLanguage || languageCode}`
      ); // Get opening times from Google
      const data = await res.json();
      setOpening(data);
    };

    fetchData();
  }, []);

  useDidMountEffect(() => {
    trackSelectedSalon({
      salonNo: selectedSalon.salonNo,
      eventName: 'select_salon',
      location: { action: router },
    });
  }, [selectedSalon]);

  return (
    <div className={styles.salonContainer}>
      <div className={styles.salonDetails}>
        {isMobile && (
          <div className={styles.mapContainer}>
            <ReactMapGL
              zoom={viewport?.zoom}
              width={isMobile ? viewport?.width : 'inherit'}
              height={isMobile ? viewport?.height : 'inherit'}
              latitude={viewport?.latitude}
              longitude={viewport?.longitude}
              mapStyle="mapbox://styles/mapbox/light-v11"
              mapboxApiAccessToken={process.env.NEXT_PUBLIC_MAPBOX_API_ACCESS_TOKEN}
              onViewportChange={newViewport => setViewport(newViewport)}
              maxZoom={16}
              minZoom={6.5}
            >
              <Marker
                key={salon?.salonName}
                longitude={salon?.position?.longitude}
                latitude={salon?.position?.latitude}
                className={cn(styles.marker, {
                  [styles.hovered]: active === idx || hoverPin === idx,
                })}
              >
                <GreenIcon
                  className={cn({
                    [styles.hovered]: active === idx || hoverPin === idx,
                  })}
                />
                <span className={cn(styles.span)}>{idx + 1}</span>
              </Marker>
            </ReactMapGL>
          </div>
        )}
        <div className={styles.backContainer}>
          <button
            type="button"
            onClick={() => {
              setDisableSearch(true);
              setShowSuggestions(false);
              setHovered(null);
              setShowSalon(false);
              setSalonFocus({});
              setSalonsInRadius(salonsInRadius);
              setActiveFilters(activeFilters);
            }}
          >
            <BackChevron width="1rem" height="0.9rem" />
            <span className={styles.backContent}>{backButtonContent}</span>
          </button>
        </div>
        {salon?.salonLoyalty && (
          <span className={styles.salonLoyalty}>
            {' '}
            <KeuneIcon className={styles.logo} /> {keuneAmbassadorSalonContent}
          </span>
        )}
        <h1 className={styles.salonName}>{salon?.salonName}</h1>
        <div className={styles.detailsContainer}>
          <span className={cn(styles.details, styles.addressMargin)}>
            <DirectionRightIcon width="2.8rem" height="2.8rem" className={styles.addMargin} />
            <span className={styles.addressContent}>
              {salon?.AddressLine2}, {salon.ZipCode || salon?.zipCode} {salon?.city},{' '}
              {salon?.country}
            </span>
          </span>
          <span className={styles.getDirections}>
            <SiteLink
              external={`https://www.google.com/maps/dir//${salon?.salonName}/@${salon?.position.longitude},${salon.position.latitude}`}
            >
              {getDirectionsPointer}
            </SiteLink>
          </span>
          {salon?.phoneNumber && (
            <SiteLink external={`tel:${salon?.phoneNumber}`}>
              <span className={cn(styles.details, styles.phoneMargin)}>
                <Phone width="2.8rem" height="2.8rem" className={styles.addMargin} />
                <span className={styles.phoneContent}>{salon?.phoneNumber}</span>
              </span>
            </SiteLink>
          )}
          {salon?.website && (
            <span className={cn(styles.details, styles.websiteMargin)}>
              <WebsiteRedirect width="2.8rem" height="2.8rem" className={styles.addMargin} />
              <SiteLink external={`${salon?.website}`}>
                <span className={styles.websiteContent}> {translations?.visitWebsitePrompt} </span>
              </SiteLink>
            </span>
          )}
          <div className={styles.filtersContainer}>
            {salon['1922'] && <span className={styles.filter}>{translations['1922Tag']}</span>}
            {salon?.keuneYou && <span className={styles.filter}>{translations?.keuneYouTag}</span>}
            {salon?.SoPure && <span className={styles.filter}>{translations?.soPureTag}</span>}
            {salon?.blend && <span className={styles.filter}>{translations?.blendTag}</span>}
            {salon?.color && <span className={styles.filter}>{translations?.keuneColorTag}</span>}
            {salon?.style && <span className={styles.filter}>{translations?.styleTag}</span>}
            {salon?.care && <span className={styles.filter}>{translations?.careTag}</span>}
          </div>
          {Object.keys(selectedSalon).length > 0 &&
          selectedSalon?.salonName === salon?.salonName ? (
            <div className={styles.selectedSalonContainer}>
              <p className={styles.selectedSalon}>
                <strong className={styles.selectedSalonName}>{selectedSalon.salonName} </strong>{' '}
                {translations?.selectedSalonDescription}
              </p>
            </div>
          ) : (
            <>
              {opening.data && (
                <div className={styles.openingTimesContainer}>
                  <div className={styles.openingTimes}>
                    {opening.data.result &&
                      Object.keys(opening.data.result).length > 0 &&
                      opening.data.result.opening_hours.weekday_text.map((day, index) => {
                        const colonIndex = day.search(/:/);
                        const dayName = day.slice(0, colonIndex);

                        return (
                          <div className={styles.weekDay} key={day}>
                            <div className={styles.day}>{dayName}</div>
                            <div className={styles.time}>
                              {getOpeningTimes(opening.data, index)}
                            </div>
                          </div>
                        );
                      })}
                  </div>{' '}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {!isMobile && (
        <div className={styles.mapContainer}>
          <ReactMapGL
            zoom={viewport?.zoom}
            width={viewport?.width}
            height={viewport?.height}
            latitude={viewport?.latitude}
            longitude={viewport?.longitude}
            mapStyle="mapbox://styles/mapbox/light-v11"
            mapboxApiAccessToken={process.env.NEXT_PUBLIC_MAPBOX_API_ACCESS_TOKEN}
            onViewportChange={newViewport => {
              setViewport(newViewport);
            }}
            maxZoom={16}
            minZoom={6.5}
          >
            <Marker
              key={salon?.salonNo}
              longitude={salon?.position.longitude}
              latitude={salon?.position.latitude}
              className={cn(styles.marker, {
                [styles.hovered]: active === idx || hoverPin === idx,
              })}
            >
              <GreenIcon
                className={cn({
                  [styles.hovered]: active === idx || hoverPin === idx,
                })}
              />
              <span className={cn(styles.span)}>{idx + 1}</span>
            </Marker>
          </ReactMapGL>
        </div>
      )}
    </div>
  );
};

export default SalonDetails;
